import React, { useRef, useEffect } from 'react';

const HeartAnimation = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let loaded = false;
    let resizeHandler;

    // Polyfill requestAnimationFrame
    window.requestAnimationFrame =
      window.__requestAnimationFrame ||
      window.requestAnimationFrame ||
      window.webkitRequestAnimationFrame ||
      window.mozRequestAnimationFrame ||
      window.oRequestAnimationFrame ||
      window.msRequestAnimationFrame ||
      ((() => {
        return (callback, element) => {
          let lastTime = element.__lastTime || 0;
          const currTime = Date.now();
          const timeToCall = Math.max(1, 33 - (currTime - lastTime));
          window.setTimeout(callback, timeToCall);
          element.__lastTime = currTime + timeToCall;
        };
      })());

    const isDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      (navigator.userAgent || navigator.vendor || window.opera).toLowerCase()
    );
    const mobile = isDevice;
    const koef = mobile ? 0.5 : 1;

    const init = () => {
      if (loaded) return;
      loaded = true;

      // sizing
      const setSize = () => {
        canvas.width = koef * window.innerWidth;
        canvas.height = koef * window.innerHeight;
        ctx.fillStyle = 'rgba(0,0,0,1)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      };
      setSize();
      window.addEventListener('resize', setSize);
      resizeHandler = setSize;

      const rand = Math.random;
      const traceCount = mobile ? 20 : 50;

      // heart shape parametric function
      const heartPosition = rad => [
        Math.pow(Math.sin(rad), 3),
        -(15 * Math.cos(rad) - 5 * Math.cos(2 * rad) - 2 * Math.cos(3 * rad) - Math.cos(4 * rad))
      ];
      const scaleAndTranslate = (pos, sx, sy, dx, dy) => [dx + pos[0] * sx, dy + pos[1] * sy];

      // generate origin points
      const dr = mobile ? 0.3 : 0.1;
      const pointsOrigin = [];
      for (let i = 0; i < Math.PI * 2; i += dr) {
        pointsOrigin.push(scaleAndTranslate(heartPosition(i), 210, 13, 0, 0));
      }
      for (let i = 0; i < Math.PI * 2; i += dr) {
        pointsOrigin.push(scaleAndTranslate(heartPosition(i), 150, 9, 0, 0));
      }
      for (let i = 0; i < Math.PI * 2; i += dr) {
        pointsOrigin.push(scaleAndTranslate(heartPosition(i), 90, 5, 0, 0));
      }
      const heartPointsCount = pointsOrigin.length;

      const targetPoints = [];
      const pulse = (kx, ky) => {
        for (let i = 0; i < pointsOrigin.length; i++) {
          const [x, y] = pointsOrigin[i];
          targetPoints[i] = [kx * x + canvas.width / 2, ky * y + canvas.height / 2];
        }
      };

      // entities
      const entities = [];
      for (let i = 0; i < heartPointsCount; i++) {
        const x = rand() * canvas.width;
        const y = rand() * canvas.height;
        const trace = Array.from({ length: traceCount }, () => ({ x, y }));
        entities.push({
          vx: 0,
          vy: 0,
          speed: rand() + 5,
          q: Math.floor(rand() * heartPointsCount),
          D: 2 * (i % 2) - 1,
          force: 0.2 * rand() + 0.7,
          f: `hsla(0,${Math.floor(rand() * 40 + 60)}%,${Math.floor(rand() * 40 + 20)}%,.3)`,
          trace
        });
      }

      const config = { traceK: 0.4, timeDelta: 0.01 };
      let time = 0;

      // main loop
      const loop = () => {
        const n = -Math.cos(time);
        pulse((1 + n) * 0.5, (1 + n) * 0.5);
        time += (Math.sin(time) < 0 ? 9 : n > 0.8 ? 0.2 : 1) * config.timeDelta;

        ctx.fillStyle = 'rgba(0,0,0,0.1)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        entities.forEach(u => {
          const [tx, ty] = targetPoints[u.q];
          const dx = u.trace[0].x - tx;
          const dy = u.trace[0].y - ty;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len < 10) {
            if (rand() > 0.95) {
              u.q = Math.floor(rand() * heartPointsCount);
            } else {
              if (rand() > 0.99) u.D *= -1;
              u.q = ((u.q + u.D) % heartPointsCount + heartPointsCount) % heartPointsCount;
            }
          }

          u.vx += (-dx / len) * u.speed;
          u.vy += (-dy / len) * u.speed;
          u.trace[0].x += u.vx;
          u.trace[0].y += u.vy;
          u.vx *= u.force;
          u.vy *= u.force;

          for (let k = 0; k < u.trace.length - 1; k++) {
            const T = u.trace[k];
            const N = u.trace[k + 1];
            N.x -= config.traceK * (N.x - T.x);
            N.y -= config.traceK * (N.y - T.y);
          }

          ctx.fillStyle = u.f;
          u.trace.forEach(p => ctx.fillRect(p.x, p.y, 1, 1));
        });

        animRef.current = window.requestAnimationFrame(loop, canvas);
      };

      loop();
    };

    if (document.readyState === 'complete' || document.readyState === 'loaded' || document.readyState === 'interactive') {
      init();
    } else {
      document.addEventListener('DOMContentLoaded', init, false);
    }

    return () => {
      cancelAnimationFrame(animRef.current);
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.2)' }}
    />
  );
};

export default HeartAnimation;
